import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Platform } from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import { PERMISSIONS, request, RESULTS } from 'react-native-permissions';

import { apiClient, handleApiError } from '@/config/api';
import {
  ClockStatusResponse,
  ClockInRequest,
  ClockOutRequest,
  TimeEntryResponse,
  TimesheetResponse,
  PayPeriodSummary,
  MissingPunchRequest,
  MissingPunchResponse,
  SwitchPunchResponse,
} from '@/types/api';
import nfcService, { NfcClockAction, NfcTagResult } from '@/services/nfcService';

export type NfcStatus = 'unavailable' | 'disabled' | 'ready' | 'scanning' | 'success' | 'error';

interface TimeClockState {
  clockStatus: ClockStatusResponse | null;
  payPeriodSummary: PayPeriodSummary | null;
  isLoading: boolean;
  isLoadingPayPeriod: boolean;
  error: string | null;
  location: {
    latitude: number | null;
    longitude: number | null;
    accuracy: number | null;
  };
  todaysPunches: TimeEntryResponse[];
  punchHistory: TimeEntryResponse[];
  nfcSupported: boolean;
  nfcEnabled: boolean;
  nfcStatus: NfcStatus;
  nfcLastAction: NfcClockAction | null;
  nfcError: string | null;
}

const initialState: TimeClockState = {
  clockStatus: null,
  payPeriodSummary: null,
  isLoading: false,
  isLoadingPayPeriod: false,
  error: null,
  location: {
    latitude: null,
    longitude: null,
    accuracy: null,
  },
  todaysPunches: [],
  punchHistory: [],
  nfcSupported: false,
  nfcEnabled: false,
  nfcStatus: 'unavailable',
  nfcLastAction: null,
  nfcError: null,
};

// Helper function to get current location
const getCurrentLocation = (): Promise<{latitude: number; longitude: number; accuracy: number}> => {
  return new Promise((resolve, reject) => {
    Geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

// Async Thunks
export const getClockStatus = createAsyncThunk(
  'timeClock/getStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<ClockStatusResponse>('/clock/status');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const clockIn = createAsyncThunk(
  'timeClock/clockIn',
  async (request: Omit<ClockInRequest, 'latitude' | 'longitude' | 'accuracy'>, { getState, rejectWithValue }) => {
    try {
      // Get current location
      let location = null;
      try {
        location = await getCurrentLocation();
      } catch (locationError) {
        console.warn('Failed to get location:', locationError);
      }

      const clockInData: ClockInRequest = {
        ...request,
        latitude: location?.latitude,
        longitude: location?.longitude,
        accuracy: location?.accuracy,
      };

      const response = await apiClient.post<ClockStatusResponse>('/clock/in', clockInData);
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const clockOut = createAsyncThunk(
  'timeClock/clockOut',
  async (request: Omit<ClockOutRequest, 'latitude' | 'longitude' | 'accuracy'>, { rejectWithValue }) => {
    try {
      // Get current location
      let location = null;
      try {
        location = await getCurrentLocation();
      } catch (locationError) {
        console.warn('Failed to get location:', locationError);
      }

      const clockOutData: ClockOutRequest = {
        ...request,
        latitude: location?.latitude,
        longitude: location?.longitude,
        accuracy: location?.accuracy,
      };

      const response = await apiClient.post<ClockStatusResponse>('/clock/out', clockOutData);
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getTodaysPunches = createAsyncThunk(
  'timeClock/getTodaysPunches',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<TimesheetResponse>('/timesheet/today');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getPunchHistory = createAsyncThunk(
  'timeClock/getPunchHistory',
  async (
    { startDate, endDate }: { startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get<TimesheetResponse>(
        `/timesheet/range?startDate=${startDate}&endDate=${endDate}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const getPayPeriodSummary = createAsyncThunk(
  'timeClock/getPayPeriodSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get<PayPeriodSummary>('/clock/pay-period');
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Add a forgotten clock-in and/or clock-out. The backend writes them PENDING
 * through iflipweb2; errors come back as a staff-facing
 * message. No slice state — the sheet owns its busy/error UI.
 */
export const addMissingPunch = createAsyncThunk(
  'timeClock/addMissingPunch',
  async (request: MissingPunchRequest, { rejectWithValue }) => {
    try {
      return await apiClient.post<MissingPunchResponse>('/punch/missing', request);
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * Switch one of the staffer's punches between clock-in and clock-out (e.g. an
 * accidental second clock-in). Written PENDING; a manager signs off behind the scenes.
 */
export const switchPunch = createAsyncThunk(
  'timeClock/switchPunch',
  async ({ punchId, reason }: { punchId: number; reason?: string }, { rejectWithValue }) => {
    try {
      return await apiClient.post<SwitchPunchResponse>(
        `/punch/${punchId}/switch`,
        reason ? { reason } : {}
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

/**
 * "I clocked out earlier — this clock-out was my next clock-in": adds the earlier
 * clock-out, switches the punch to a clock-in, and (optionally) adds the clock-out
 * that closes the next stretch. Same response shape as a missing punch.
 */
export const splitPunch = createAsyncThunk(
  'timeClock/splitPunch',
  async (
    { punchId, clockOut, nextClockOut }: { punchId: number; clockOut: string; nextClockOut?: string },
    { rejectWithValue }
  ) => {
    try {
      return await apiClient.post<MissingPunchResponse>(`/punch/${punchId}/split`, {
        clockOut,
        ...(nextClockOut ? { nextClockOut } : {}),
      });
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const requestLocationPermission = createAsyncThunk(
  'timeClock/requestLocationPermission',
  async (_, { rejectWithValue }) => {
    try {
      const permission = Platform.OS === 'ios'
        ? PERMISSIONS.IOS.LOCATION_WHEN_IN_USE
        : PERMISSIONS.ANDROID.ACCESS_FINE_LOCATION;
      const result = await request(permission);
      
      if (result === RESULTS.GRANTED) {
        const location = await getCurrentLocation();
        return location;
      } else {
        return rejectWithValue('Location permission denied');
      }
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

// NFC Thunks
export const initNfc = createAsyncThunk(
  'timeClock/initNfc',
  async (_, { rejectWithValue }) => {
    try {
      const supported = await nfcService.isSupported();
      if (!supported) {
        return { supported: false, enabled: false };
      }
      const enabled = await nfcService.isEnabled();
      if (enabled) {
        await nfcService.init();
      }
      return { supported, enabled };
    } catch (error) {
      return rejectWithValue('Failed to initialize NFC');
    }
  }
);

export const nfcClockAction = createAsyncThunk(
  'timeClock/nfcClockAction',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await nfcService.readClockTag();
      if (!result) {
        return rejectWithValue('Unrecognized NFC tag. Use an SGA clock tag.');
      }

      // Get location in background (non-blocking)
      let location: { latitude: number; longitude: number; accuracy: number } | null = null;
      try {
        location = await getCurrentLocation();
      } catch {
        // Location is optional for NFC clock
      }

      const requestData = {
        verificationMethod: 'NFC',
        notes: `NFC tap (tag: ${result.tagId})`,
        latitude: location?.latitude,
        longitude: location?.longitude,
        accuracy: location?.accuracy,
      };

      if (result.action === 'CLOCK_IN') {
        const response = await apiClient.post<ClockStatusResponse>('/clock/in', requestData);
        dispatch(getTodaysPunches());
        return { action: result.action, clockStatus: response };
      } else {
        const response = await apiClient.post<ClockStatusResponse>('/clock/out', requestData);
        dispatch(getTodaysPunches());
        return { action: result.action, clockStatus: response };
      }
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

export const cancelNfcScan = createAsyncThunk(
  'timeClock/cancelNfcScan',
  async () => {
    nfcService.cancelRead();
    return true;
  }
);

export const writeNfcTag = createAsyncThunk(
  'timeClock/writeNfcTag',
  async (action: NfcClockAction, { rejectWithValue }) => {
    try {
      const success = await nfcService.writeClockTag(action);
      if (!success) {
        return rejectWithValue('Failed to write NFC tag. Make sure the tag is close to the device.');
      }
      return action;
    } catch (error) {
      return rejectWithValue('Failed to write NFC tag');
    }
  }
);

// Slice
const timeClockSlice = createSlice({
  name: 'timeClock',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLocation: (state, action: PayloadAction<{latitude: number; longitude: number; accuracy: number}>) => {
      state.location = action.payload;
    },
    clearPunchHistory: (state) => {
      state.punchHistory = [];
    },
    clearNfcError: (state) => {
      state.nfcError = null;
    },
    resetNfcStatus: (state) => {
      state.nfcStatus = state.nfcEnabled ? 'ready' : (state.nfcSupported ? 'disabled' : 'unavailable');
      state.nfcLastAction = null;
      state.nfcError = null;
    },
  },
  extraReducers: (builder) => {
    // Get Clock Status
    builder
      .addCase(getClockStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getClockStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clockStatus = action.payload;
      })
      .addCase(getClockStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Clock In
    builder
      .addCase(clockIn.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clockIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clockStatus = action.payload;
      })
      .addCase(clockIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Clock Out
    builder
      .addCase(clockOut.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(clockOut.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clockStatus = action.payload;
      })
      .addCase(clockOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Today's Punches
    builder
      .addCase(getTodaysPunches.fulfilled, (state, action) => {
        state.todaysPunches = action.payload.todaysEntries || [];
      })
      .addCase(getTodaysPunches.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Punch History
    builder
      .addCase(getPunchHistory.fulfilled, (state, action) => {
        state.punchHistory = action.payload.timeEntries || [];
      })
      .addCase(getPunchHistory.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Pay Period Summary
    builder
      .addCase(getPayPeriodSummary.pending, (state) => {
        state.isLoadingPayPeriod = true;
        state.error = null;
      })
      .addCase(getPayPeriodSummary.fulfilled, (state, action) => {
        state.isLoadingPayPeriod = false;
        state.payPeriodSummary = action.payload;
      })
      .addCase(getPayPeriodSummary.rejected, (state, action) => {
        state.isLoadingPayPeriod = false;
        state.error = action.payload as string;
      });

    // Location Permission
    builder
      .addCase(requestLocationPermission.fulfilled, (state, action) => {
        state.location = action.payload;
      })
      .addCase(requestLocationPermission.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // NFC Init
    builder
      .addCase(initNfc.fulfilled, (state, action) => {
        state.nfcSupported = action.payload.supported;
        state.nfcEnabled = action.payload.enabled;
        state.nfcStatus = action.payload.enabled ? 'ready' : (action.payload.supported ? 'disabled' : 'unavailable');
      })
      .addCase(initNfc.rejected, (state) => {
        state.nfcSupported = false;
        state.nfcEnabled = false;
        state.nfcStatus = 'unavailable';
      });

    // NFC Clock Action (read tag + clock in/out)
    builder
      .addCase(nfcClockAction.pending, (state) => {
        state.nfcStatus = 'scanning';
        state.nfcError = null;
        state.nfcLastAction = null;
      })
      .addCase(nfcClockAction.fulfilled, (state, action) => {
        state.nfcStatus = 'success';
        state.nfcLastAction = action.payload.action;
        state.clockStatus = action.payload.clockStatus;
        state.nfcError = null;
      })
      .addCase(nfcClockAction.rejected, (state, action) => {
        state.nfcStatus = 'error';
        state.nfcError = action.payload as string;
      });

    // Cancel NFC Scan
    builder
      .addCase(cancelNfcScan.fulfilled, (state) => {
        state.nfcStatus = 'ready';
      });

    // Write NFC Tag
    builder
      .addCase(writeNfcTag.pending, (state) => {
        state.nfcStatus = 'scanning';
        state.nfcError = null;
      })
      .addCase(writeNfcTag.fulfilled, (state) => {
        state.nfcStatus = 'success';
        state.nfcError = null;
      })
      .addCase(writeNfcTag.rejected, (state, action) => {
        state.nfcStatus = 'error';
        state.nfcError = action.payload as string;
      });
  },
});

export const { clearError, setLocation, clearPunchHistory, clearNfcError, resetNfcStatus } = timeClockSlice.actions;
export default timeClockSlice.reducer;